/**
 * Admin 「외부 연동」 — 파트너 목록 `/admin/partners`.
 *
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면 — 「화면 설계 › 파트너 목록」, D-9
 * @spec:2026-10-15-KMRS-접근코드-PhAI-Admin-관리-전체기관 — 파트너 표 아래 「K-MRS 약사 접근코드」 영역(사고 재구성 ①1)
 *
 * 비즈니스 로직(조회·등록·이동)은 여기서, 표시는 PartnerTable·PartnerFormModal에 맡긴다.
 * K-MRS 접근코드 영역은 파트너와 독립이라 표 아래에 항상 한 번 렌더한다(KmrsAccessCodeSection).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/atoms/Button";
import { ConfirmManager } from "components/organisms/CommonUI";
import { useCreatePartner, usePartnerList, usePartnerMeta } from "hooks/partners/usePartners";
import { buildCreateBody, isRelEnv } from "utils/partnerAdmin";
import EnvBadge from "./EnvBadge";
import PartnerTable from "./PartnerTable";
import PartnerFormModal from "./PartnerFormModal";
import KmrsAccessCodeSection from "./kmrs/KmrsAccessCodeSection";
import { EmptyText, Notice, PageHeader, PageTitle, PageWrap, TitleRow } from "./style";

const PartnersPage: React.FC = () => {
  const navigate = useNavigate();
  const meta = usePartnerMeta();
  const list = usePartnerList();
  const createPartner = useCreatePartner();

  const openCreate = () => {
    if (!meta.data) return;
    ConfirmManager.show({
      body: (
        <PartnerFormModal
          meta={meta.data}
          onSubmit={async (values) => {
            const created = await createPartner.mutateAsync(buildCreateBody(values));
            ConfirmManager.hide();
            navigate(`/admin/partners/${created.id}`);
          }}
          onCancel={() => ConfirmManager.hide()}
        />
      ),
      showBackdrop: true,
      backdropClose: false,
      showCloseButton: false,
    });
  };

  return (
    <PageWrap data-testid="partners-page">
      <PageHeader>
        <TitleRow>
          <PageTitle>외부 연동</PageTitle>
          <EnvBadge env={meta.data?.env} />
        </TitleRow>
        <Button
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          onClick={openCreate}
          disabled={!meta.data}
          data-testid="partner-create"
        >
          파트너 등록
        </Button>
      </PageHeader>

      {meta.isError && <Notice $tone="warn">{meta.error?.message}</Notice>}

      {list.isLoading ? (
        <EmptyText>불러오는 중입니다.</EmptyText>
      ) : list.isError ? (
        <Notice $tone="warn">
          {list.error?.message}{" "}
          <Button type="Text" size="XSmall" variant="Ghost" color="Secondary" onClick={() => list.refetch()}>
            다시 불러오기
          </Button>
        </Notice>
      ) : (
        <PartnerTable
          partners={list.data ?? []}
          meta={meta.data}
          onRowClick={(p) => navigate(`/admin/partners/${p.id}`)}
        />
      )}

      {/*
        K-MRS 약사 접근코드 — 파트너 표 아래 독립 영역. 파트너 유무·종류와 무관하게 항상 한 번
        렌더한다(특정 파트너 상세가 아님). isRel은 헤더 EnvBadge와 같은 meta.data.env에서 계산한다.
       */}
      <KmrsAccessCodeSection isRel={isRelEnv(meta.data?.env)} />
    </PageWrap>
  );
};

export default PartnersPage;
